import { businessToday, type UsuryRate } from '@paguz/contracts';
import { AmountInput, Badge, Button, cn, toast } from '@paguz/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { HistoryIcon, LockIcon, RotateCcwIcon, SaveIcon } from 'lucide-react';
import { useId, useMemo, useState, type ReactNode } from 'react';
import type { SettingKey, SettingValue, Versioned } from '../../api/admin-schemas.ts';
import { ApiError } from '../../api/client.ts';
import { api, queryKeys } from '../../api/endpoints.ts';
import { ConfirmActionDialog } from '../../components/confirm-dialog.tsx';
import { Field, SuffixInput } from '../../components/form-controls.tsx';
import { ErrorState, LoadingRows, Panel } from '../../components/page.tsx';
import {
  formatDateTime,
  fromMoneyAmount,
  percentInputToRate,
  rateToPercentInput,
  toMoneyAmount,
} from '../../lib/format.ts';
import { useCan } from '../../lib/session.ts';
import { diffValues, formatValue, setIn, type Change } from './diff.ts';
import { currentUsuryRate, validateSetting, type FieldErrors } from './validation.ts';

export type Path = readonly (string | number)[];

export interface SettingEditor<K extends SettingKey> {
  key: K;
  draft: SettingValue<K>;
  saved: Versioned<SettingValue<K>>;
  set: (path: Path, value: unknown) => void;
  errors: FieldErrors;
  error: (path: Path) => string | undefined;
  changes: Change[];
  canEdit: boolean;
}

/** Usura vigente (para validar tasas del producto) según el reloj de demo. */
export function useUsury(): { usury: UsuryRate | null | undefined; rates: readonly UsuryRate[] } {
  const rates = useQuery({ queryKey: queryKeys.usuryRates, queryFn: api.listUsuryRates });
  const clock = useQuery({ queryKey: queryKeys.clock, queryFn: api.getClock });
  const product = useQuery({
    queryKey: queryKeys.setting('product'),
    queryFn: () => api.getSetting('product'),
  });
  if (!rates.data || !clock.data || !product.data)
    return { usury: undefined, rates: rates.data?.data ?? [] };
  const today = businessToday({ now: () => new Date(clock.data.now) });
  return {
    usury: currentUsuryRate(rates.data.data, product.data.value.usuryModality, today),
    rates: rates.data.data,
  };
}

/**
 * Editor de una sección de configuración versionada: borrador local, validación (esquema de
 * contracts + reglas de negocio), diff y guardado con doble confirmación y motivo.
 */
export function SettingSection<K extends SettingKey>({
  settingKey,
  title,
  description,
  labels,
  readOnly = false,
  aside,
  children,
}: {
  settingKey: K;
  title: string;
  description: ReactNode;
  labels: Readonly<Record<string, string>>;
  readOnly?: boolean;
  aside?: (editor: SettingEditor<K>) => ReactNode;
  children: (editor: SettingEditor<K>) => ReactNode;
}) {
  const query = useQuery({
    queryKey: queryKeys.setting(settingKey),
    queryFn: () => api.getSetting(settingKey),
  });
  if (query.isPending) return <LoadingRows rows={8} />;
  if (query.isError) return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
  return (
    <Editor
      key={query.data.version}
      settingKey={settingKey}
      saved={query.data}
      title={title}
      description={description}
      labels={labels}
      readOnly={readOnly}
      aside={aside}
    >
      {children}
    </Editor>
  );
}

function Editor<K extends SettingKey>({
  settingKey,
  saved,
  title,
  description,
  labels,
  readOnly,
  aside,
  children,
}: {
  settingKey: K;
  saved: Versioned<SettingValue<K>>;
  title: string;
  description: ReactNode;
  labels: Readonly<Record<string, string>>;
  readOnly: boolean;
  aside: ((editor: SettingEditor<K>) => ReactNode) | undefined;
  children: (editor: SettingEditor<K>) => ReactNode;
}) {
  const queryClient = useQueryClient();
  const canEditRole = useCan('config:edit');
  const canEdit = canEditRole && !readOnly;
  const [draft, setDraft] = useState<SettingValue<K>>(() => structuredClone(saved.value));
  const [confirming, setConfirming] = useState(false);
  const [serverErrors, setServerErrors] = useState<FieldErrors>({});
  const { usury } = useUsury();
  const product = useQuery({
    queryKey: queryKeys.setting('product'),
    queryFn: () => api.getSetting('product'),
    enabled: settingKey === 'decisionPolicy',
  });

  const changes = useMemo(() => diffValues(saved.value, draft), [saved.value, draft]);
  const errors = useMemo(
    () => ({
      ...validateSetting(settingKey, draft, {
        ...(usury !== undefined && { usury }),
        ...(product.data && { product: product.data.value }),
      }),
      ...serverErrors,
    }),
    [settingKey, draft, usury, product.data, serverErrors],
  );
  const hasErrors = Object.keys(errors).length > 0;

  const mutation = useMutation({
    mutationFn: (reason: string) => api.putSetting(settingKey, draft, reason),
    onSuccess: (next) => {
      queryClient.setQueryData(queryKeys.setting(settingKey), next);
      void queryClient.invalidateQueries({ queryKey: ['audit'] });
      toast.success(
        next.version > 0
          ? `${title}: versión ${next.version} vigente`
          : `${title}: cambios guardados`,
      );
    },
    onError: (error) => {
      if (error instanceof ApiError && error.problem?.errors) {
        setServerErrors(Object.fromEntries(error.problem.errors.map((e) => [e.path, e.message])));
      }
    },
  });

  const editor: SettingEditor<K> = {
    key: settingKey,
    draft,
    saved,
    set: (path, value) => {
      setServerErrors({});
      setDraft((prev) => setIn(prev, path, value));
    },
    errors,
    error: (path) => errors[path.join('.')],
    changes,
    canEdit,
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <h2 className="font-display text-2xl font-semibold text-foreground">{title}</h2>
          <p className="mt-1 max-w-3xl text-muted-foreground">{description}</p>
          <p className="mt-2 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
            {saved.version > 0 ? (
              <>
                <Badge variant="secondary">Versión {saved.version}</Badge>
                vigente desde {formatDateTime(saved.effectiveFrom)} · por {saved.updatedBy}
              </>
            ) : (
              <Badge variant="secondary">
                Configuración vigente · cambios registrados en auditoría
              </Badge>
            )}
            {!canEdit && (
              <Badge variant="outline">
                <LockIcon /> {readOnly ? 'Solo lectura en la consola' : 'Solo lectura para tu rol'}
              </Badge>
            )}
          </p>
        </div>
        {canEdit && (
          <div className="flex items-center gap-2">
            {changes.length > 0 && (
              <span className="text-sm font-semibold text-highlight">
                {changes.length} {changes.length === 1 ? 'cambio' : 'cambios'} sin guardar
              </span>
            )}
            <Button
              variant="ghost"
              disabled={changes.length === 0}
              onClick={() => {
                setServerErrors({});
                setDraft(structuredClone(saved.value));
              }}
            >
              <RotateCcwIcon /> Descartar
            </Button>
            <Button
              variant="highlight"
              disabled={changes.length === 0 || hasErrors}
              title={hasErrors ? 'Corrige los campos marcados antes de guardar.' : undefined}
              onClick={() => {
                setConfirming(true);
              }}
            >
              <SaveIcon /> Guardar cambios
            </Button>
          </div>
        )}
      </div>

      {hasErrors && changes.length > 0 && (
        <p
          role="alert"
          className="rounded-xl bg-danger-soft px-4 py-3 text-sm text-danger-soft-foreground"
        >
          Hay {Object.keys(errors).length} {Object.keys(errors).length === 1 ? 'campo' : 'campos'}{' '}
          por corregir: {Object.values(errors)[0]}
        </p>
      )}

      <div className={cn('grid gap-6', aside && '2xl:grid-cols-[minmax(0,1fr)_26rem]')}>
        <fieldset disabled={!canEdit} className="flex min-w-0 flex-col gap-6">
          {children(editor)}
        </fieldset>
        {aside && (
          <div className="order-first flex min-w-0 flex-col gap-6 2xl:order-none">
            {aside(editor)}
          </div>
        )}
      </div>

      {saved.history.length > 0 && (
        <Panel
          title="Historial de versiones"
          description="Los cambios aplican a nuevas operaciones desde su vigencia."
        >
          <ol className="flex flex-col gap-3">
            {saved.history.map((h) => (
              <li key={h.version} className="flex gap-3 text-sm">
                <HistoryIcon className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
                <div>
                  <p>
                    <span className="font-semibold">v{h.version}</span> ·{' '}
                    {formatDateTime(h.effectiveFrom)} · {h.changedBy}
                  </p>
                  <p className="text-muted-foreground">{h.reason}</p>
                </div>
              </li>
            ))}
          </ol>
        </Panel>
      )}

      <ConfirmActionDialog
        open={confirming}
        onOpenChange={setConfirming}
        title={`Guardar ${title.toLowerCase()}`}
        description={
          saved.version > 0
            ? `Se crea la versión ${saved.version + 1}, vigente de inmediato para nuevas operaciones.`
            : 'Aplica de inmediato a nuevas operaciones.'
        }
        confirmLabel="Guardar nueva versión"
        reasonLabel="Motivo del cambio (queda en auditoría)"
        summary={<ChangeList changes={changes} labels={labels} />}
        onConfirm={async (reason) => {
          await mutation.mutateAsync(reason);
        }}
      />
    </div>
  );
}

export function ChangeList({
  changes,
  labels,
}: {
  changes: readonly Change[];
  labels: Readonly<Record<string, string>>;
}) {
  return (
    <div>
      <p className="mb-2 font-semibold text-foreground">Resumen de cambios</p>
      <ul className="flex flex-col gap-1.5">
        {changes.map((change) => (
          <li key={change.path} className="grid grid-cols-[minmax(0,1fr)_auto] gap-3">
            <span className="text-muted-foreground">{labels[change.path] ?? change.path}</span>
            <span className="text-right font-medium">
              <span className="text-muted-foreground line-through">
                {formatValue(change.before)}
              </span>{' '}
              → <span className="text-foreground">{formatValue(change.after)}</span>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ── Campos ────────────────────────────────────────────────────────────────
/** Tasa decimal (`"0.450000"`) editada como porcentaje (`45,00`). */
export function RateField({
  label,
  value,
  onChange,
  error,
  hint,
  suffix = '%',
}: {
  label: string;
  value: string;
  onChange: (rate: string) => void;
  error?: string | undefined;
  hint?: ReactNode;
  suffix?: string;
}) {
  const id = useId();
  const [text, setText] = useState(() => rateToPercentInput(value));
  const [previous, setPrevious] = useState(value);
  const [invalid, setInvalid] = useState(false);
  // Si el valor cambia desde afuera (p. ej. "Descartar"), se adopta.
  if (value !== previous) {
    setPrevious(value);
    if (percentInputToRate(text) !== value) {
      setText(rateToPercentInput(value));
      setInvalid(false);
    }
  }
  return (
    <Field
      label={label}
      htmlFor={id}
      error={invalid ? 'Escribe un porcentaje válido (p. ej. 45,21).' : error}
      hint={hint}
    >
      <SuffixInput
        id={id}
        suffix={suffix}
        value={text}
        aria-invalid={invalid || error ? true : undefined}
        onChange={(event) => {
          const next = event.target.value;
          setText(next);
          const rate = percentInputToRate(next);
          setInvalid(rate === null);
          if (rate !== null) onChange(rate);
        }}
      />
    </Field>
  );
}

export function IntField({
  label,
  value,
  onChange,
  error,
  hint,
  suffix,
  min,
  max,
}: {
  label: string;
  value: number;
  onChange: (value: number) => void;
  error?: string | undefined;
  hint?: ReactNode;
  suffix: string;
  min?: number;
  max?: number;
}) {
  const id = useId();
  return (
    <Field label={label} htmlFor={id} error={error} hint={hint}>
      <SuffixInput
        id={id}
        suffix={suffix}
        inputMode="numeric"
        value={Number.isNaN(value) ? '' : String(value)}
        aria-invalid={error ? true : undefined}
        min={min}
        max={max}
        onChange={(event) => {
          const digits = event.target.value.replace(/\D/g, '');
          onChange(digits === '' ? Number.NaN : Number.parseInt(digits, 10));
        }}
      />
    </Field>
  );
}

export function MoneyField({
  label,
  value,
  onChange,
  error,
  hint,
}: {
  label: string;
  value: { amount: string; currency: 'COP' };
  onChange: (value: { amount: string; currency: 'COP' }) => void;
  error?: string | undefined;
  hint?: ReactNode;
}) {
  const id = useId();
  return (
    <Field label={label} htmlFor={id} error={error} hint={hint}>
      <AmountInput
        id={id}
        className="h-10 text-sm"
        value={fromMoneyAmount(value.amount)}
        invalid={error !== undefined}
        onValueChange={(next) => {
          onChange({ amount: toMoneyAmount(next), currency: 'COP' });
        }}
      />
    </Field>
  );
}

/** Grupo de campos dentro de una sección. */
export function FormCard({
  title,
  description,
  children,
  actions,
}: {
  title: string;
  description?: ReactNode;
  children: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <Panel title={title} description={description} actions={actions}>
      <div className="flex flex-col gap-5">{children}</div>
    </Panel>
  );
}
